import React, { useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useTranslation } from '../hooks/useTranslation';
import { ProviderType } from '../lib/provider';
import { NestQAvatar } from '../components/NestQAvatar';
import { Eye, EyeOff } from 'lucide-react';

export const RegisterPage: React.FC = () => {
    const { session, signInMock, signUpReal, isLoading } = useAuth();
    const { t } = useTranslation();
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const nextParam = params.get('next');
    const [submitting, setSubmitting] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const [email, setEmail] = useState(() => {
        return params.get('email') ?? '';
    });
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');

    const [role, setRole] = useState<'parent' | 'kid'>('parent');
    const [displayName, setDisplayName] = useState('');

    if (isLoading) return <div className="p-8 text-center text-slate-500 font-bold">{t.common.loading}</div>;

    if (session.isAuthenticated) {
        if (nextParam && nextParam.startsWith('/')) {
            return <Navigate to={nextParam} replace />;
        }
        if (session.user) {
            return <Navigate to={session.user.role === 'parent' ? '/parent' : '/kid'} replace />;
        } else {
            return <Navigate to="/onboarding" replace />;
        }
    }

    const handleRegister = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setSubmitting(true);
        try {
            if (ProviderType === 'mock') {
                if (!displayName) {
                    setError('Display name is required');
                    setSubmitting(false);
                    return;
                }
                const mockHhId = 'demo-household';
                await signInMock(role, mockHhId, displayName);
            } else {
                await signUpReal(email, password);
                setError('Account created! Please Sign In.');
                setTimeout(() => {
                    const nextPart = nextParam ? `&next=${encodeURIComponent(nextParam)}` : '';
                    window.location.href = `/login?email=${encodeURIComponent(email)}${nextPart}`;
                }, 800);
            }
        } catch (err: any) {
            setError(err.message || 'Authentication failed');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen flex">
            <div className="hidden lg:flex lg:w-1/2 bg-primary-600 items-center justify-center">
                <div className="text-center">
                    <NestQAvatar size={320} />
                </div>
            </div>

            <div className="w-full lg:w-1/2 bg-slate-50 flex items-center justify-center p-4">
                <div className="max-w-md w-full bg-white rounded-xl shadow-sm border border-slate-200 p-8">
                    <div className="flex flex-col items-center mb-8">
                        <img
                            src="/NestQ_logo_2026.png"
                            alt="NestQ"
                            className="h-12 rounded-lg mb-4"
                        />
                        <p className="text-slate-600 text-center font-medium">
                            Your home will never be the same.
                            <span className="block">Let's start building your Nest now!</span>
                        </p>
                    </div>

                    {error && (
                        <div className={`p-3 rounded-lg text-sm mb-6 border ${error.includes('created') ? 'bg-green-50 text-green-600 border-green-100' : 'bg-red-50 text-red-600 border-red-100'}`}>
                            {error}
                        </div>
                    )}

                    <form onSubmit={handleRegister} className="space-y-4">
                        {ProviderType === 'mock' && (
                            <>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-2">Select Role</label>
                                    <div className="grid grid-cols-2 gap-4">
                                        <button
                                            type="button"
                                            onClick={() => setRole('parent')}
                                            className={`flex flex-col items-center justify-center p-4 rounded-lg border-2 transition-all ${role === 'parent' ? 'border-primary-600 bg-primary-50 text-primary-700' : 'border-slate-100 bg-white text-slate-500 hover:border-slate-200'}`}
                                        >
                                            <div className="text-2xl mb-1">👑</div>
                                            <span className="font-semibold text-xs">Parent</span>
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setRole('kid')}
                                            className={`flex flex-col items-center justify-center p-4 rounded-lg border-2 transition-all ${role === 'kid' ? 'border-purple-600 bg-purple-50 text-purple-700' : 'border-slate-100 bg-white text-slate-500 hover:border-slate-200'}`}
                                        >
                                            <div className="text-2xl mb-1">🎈</div>
                                            <span className="font-semibold text-xs">Kid</span>
                                        </button>
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">Display Name</label>
                                    <input
                                        type="text"
                                        required
                                        value={displayName}
                                        onChange={(e) => setDisplayName(e.target.value)}
                                        placeholder={role === 'parent' ? 'e.g. Mom' : 'e.g. Ben'}
                                        className="w-full px-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-primary-500 outline-none"
                                    />
                                </div>
                            </>
                        )}

                        {ProviderType === 'supabase' && (
                            <>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">{t.login.email}</label>
                                    <input
                                        type="email"
                                        required
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="w-full px-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-primary-500 outline-none"
                                        placeholder="name@example.com"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">{t.login.password}</label>
                                    <div className="relative">
                                        <input
                                            type={showPassword ? 'text' : 'password'}
                                            required
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            className="w-full px-4 py-2 pr-12 rounded-lg border border-slate-300 focus:ring-2 focus:ring-primary-500 outline-none"
                                            placeholder="••••••••"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword((v) => !v)}
                                            aria-label={showPassword ? 'Hide password' : 'Show password'}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                        >
                                            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}

                        <button
                            type="submit"
                            disabled={submitting}
                            className={`w-full bg-primary-600 text-white font-semibold py-3 rounded-lg transition-colors shadow-sm mt-4 ${submitting ? 'opacity-70 cursor-not-allowed' : 'hover:bg-primary-700'}`}
                        >
                            {submitting ? t.common.loading : (ProviderType === 'mock' ? 'Enter NestQ' : t.login.signUp)}
                        </button>
                    </form>

                    <div className="mt-6 text-center text-sm text-slate-600">
                        <p>Wait! You already have an account?</p>
                        <a
                            href={nextParam ? `/login?next=${encodeURIComponent(nextParam)}` : '/login'}
                            className="font-semibold text-primary-600 hover:text-primary-700 mt-2 underline"
                        >
                            {t.login.signIn}
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
};
